import React, { useContext, createContext, useEffect, useState, useRef, useCallback } from "react";
import { auth, db } from "./firebase";
import { signOut } from "firebase/auth";
import { doc, getDoc } from "firebase/firestore";
import { clearPatientDirectory } from "./patientDirectoryService";
import {
  signOutDueToTimeout,
  SESSION_TIMEOUT_MS,
  HARD_TIMEOUT_MS,
  SESSION_STATE,
  getSessionState,
  getGraceTimeRemaining,
  getWarningTimeRemaining,
  extendSession,
} from "./sessionTimeout";

const AuthContext = createContext();

export const useAuth = () => useContext(AuthContext);

export const AuthProvider = ({ children }) => {
  const [currentUser, setCurrentUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isVerifying, setIsVerifying] = useState(false);
  const [accountRecord, setAccountRecord] = useState(null);
  const verifiedUid = useRef(null);
  const verificationGeneration = useRef(0);
  const nextAccountRead = useRef(0);
  const lastAcceptedAccountRead = useRef(0);

  // Order successful reads by when they started, not when the network returns.
  // Failed reads do not supersede a valid response that is still in flight.
  const acceptAccountRead = useCallback((readId) => {
    if (readId < lastAcceptedAccountRead.current) return false;
    lastAcceptedAccountRead.current = readId;
    return true;
  }, []);

  // Session timeout state (consumed by AppShell)
  const [sessionState, setSessionState] = useState(null);
  const [graceTimeRemaining, setGraceTimeRemaining] = useState(null);
  const [warningTimeRemaining, setWarningTimeRemaining] = useState(null);

  const sessionCheckInterval = useRef(null);
  const activeOperationRef = useRef(false);
  const lastNotifiedStateRef = useRef(null);

  // Reuse the record that passed authentication checks. Refresh only when a
  // workflow changes it, rather than blocking every Dashboard visit on a read.
  const refreshAccount = useCallback(async () => {
    const uid = verifiedUid.current;
    const generation = verificationGeneration.current;
    if (!uid || auth.currentUser?.uid !== uid) return null;
    const readId = ++nextAccountRead.current;
    const snapshot = await getDoc(doc(db, "users", uid));
    if (verifiedUid.current !== uid || verificationGeneration.current !== generation
      || auth.currentUser?.uid !== uid) return null;
    if (!acceptAccountRead(readId)) return null;
    if (!snapshot.exists() || snapshot.data().disabled === true) {
      verifiedUid.current = null;
      setAccountRecord(null);
      setCurrentUser(null);
      await auth.signOut();
      return null;
    }
    const data = snapshot.data();
    setAccountRecord({ uid, data });
    return data;
  }, [acceptAccountRead]);

  /** AppShell calls this to signal active recording/processing */
  const setActiveOperation = useCallback((active) => {
    activeOperationRef.current = active;
  }, []);

  /**
   * Extend session from warning CTA.
   * On success, clear WARNING locally immediately so the banner dismisses without waiting for poll.
   */
  const extendSessionOnAction = useCallback(async () => {
    if (!auth.currentUser) {
      throw new Error("No authenticated user");
    }

    const state = sessionState;
    if (state !== SESSION_STATE.ACTIVE && state !== SESSION_STATE.WARNING) {
      throw new Error("Session is no longer extendable");
    }

    const uid = auth.currentUser.uid;
    const generation = verificationGeneration.current;
    const didExtend = await extendSession(uid);
    if (verifiedUid.current !== uid || generation !== verificationGeneration.current) return false;
    if (!didExtend) {
      throw new Error("Failed to extend session");
    }

    // Optimistic local flip so the WARNING banner dismisses immediately.
    setSessionState(SESSION_STATE.ACTIVE);
    setGraceTimeRemaining(null);
    setWarningTimeRemaining(null);

    // Force a one-off refresh now instead of waiting up to 30s for the poll tick.
    try {
      const userDocRef = doc(db, "users", uid);
      const readId = ++nextAccountRead.current;
      const userDoc = await getDoc(userDocRef);
      if (verifiedUid.current !== uid || generation !== verificationGeneration.current || auth.currentUser?.uid !== uid) return false;
      if (!acceptAccountRead(readId)) return false;
      if (userDoc.exists()) {
        const userData = userDoc.data();
        if (userData.disabled === true) {
          await auth.signOut();
          return false;
        }
        setAccountRecord({ uid, data: userData });
        if (userData.sessionStartTime) {
          const refreshedState = getSessionState(userData.sessionStartTime);
          setSessionState(refreshedState);
          setGraceTimeRemaining(getGraceTimeRemaining(userData.sessionStartTime));
          setWarningTimeRemaining(getWarningTimeRemaining(userData.sessionStartTime));
        }
      }
    } catch (error) {
      console.error("[authContext] Failed to refresh session state after extend:", error);
    }

    return true;
  }, [sessionState, acceptAccountRead]);

  // Start session timeout monitoring
  const startSessionMonitoring = useCallback(() => {
    // Clear any existing interval
    if (sessionCheckInterval.current) {
      clearInterval(sessionCheckInterval.current);
    }

    // Check session every 30 seconds
    sessionCheckInterval.current = setInterval(async () => {
      if (auth.currentUser) {
        try {
          // Get fresh user data from Firestore
          const uid = auth.currentUser.uid;
          const generation = verificationGeneration.current;
          const userDocRef = doc(db, "users", uid);
          const readId = ++nextAccountRead.current;
          const userDoc = await getDoc(userDocRef);
          if (verifiedUid.current !== uid || verificationGeneration.current !== generation
            || auth.currentUser?.uid !== uid) return;
          if (!acceptAccountRead(readId)) return;

          if (userDoc.exists()) {
            const userData = userDoc.data();

            // Check if user has been disabled
            if (userData.disabled === true) {
              console.log("User account disabled during session, signing out");
              await auth.signOut();
              return;
            }

            setAccountRecord({ uid, data: userData });

            if (userData.sessionStartTime) {
              const state = getSessionState(userData.sessionStartTime);
              const remaining = getGraceTimeRemaining(userData.sessionStartTime);
              const warnRemaining = getWarningTimeRemaining(userData.sessionStartTime);

              setSessionState(state);
              setGraceTimeRemaining(remaining);
              setWarningTimeRemaining(warnRemaining);

              if (state === SESSION_STATE.EXPIRED) {
                // Active operation protection — delay logout while recording/processing
                if (activeOperationRef.current) {
                  console.log("[authContext] Session expired but active operation in progress — deferring logout");
                  return;
                }
                console.log("Session expired (hard timeout), signing out user");
                await signOutDueToTimeout();
              }
              // GRACE / GRACE_URGENT / WARNING / ACTIVE — stay logged in
            }
          }
        } catch (error) {
          console.error("Error checking session:", error);
        }
      }
    }, 30000); // Check every 30 seconds
  }, [acceptAccountRead]);

  // Stop session timeout monitoring
  const stopSessionMonitoring = useCallback(() => {
    if (sessionCheckInterval.current) {
      clearInterval(sessionCheckInterval.current);
      sessionCheckInterval.current = null;
    }
  }, []);

  useEffect(() => {
    let disposed = false;
    const verifySession = async (user) => {
      const generation = ++verificationGeneration.current;
      const isCurrent = () => !disposed && generation === verificationGeneration.current;
      stopSessionMonitoring();
      verifiedUid.current = null;
      setAccountRecord(null);
      setSessionState(null);
      setGraceTimeRemaining(null);
      setWarningTimeRemaining(null);
      // An Auth account alone is not an application account. Keep the signup
      // request authenticated, but do not mount the app until provisioning ends.
      if (user && sessionStorage.getItem('signupInProgress') === 'true') {
        setCurrentUser(null);
        setIsVerifying(false);
        setLoading(false);
        return;
      }
      if (user) {
        setIsVerifying(true);
        setCurrentUser(null); // Clear current user while verifying

        // Check if user exists in Firestore and is verified
        try {
          const userDocRef = doc(db, "users", user.uid);
          const userDoc = await getDoc(userDocRef);
          if (!isCurrent()) return;

          if (userDoc.exists()) {
            const userData = userDoc.data();

            // Check if user account is disabled
            if (userData.disabled === true) {
              console.log("User account is disabled, signing out");
              await auth.signOut();
              if (!isCurrent()) return;
              setCurrentUser(null);
              setIsVerifying(false);
              setLoading(false);
              return;
            }

            // Check if this is a fresh login by examining the token
            const tokenResult = await user.getIdTokenResult();
            if (!isCurrent()) return;
            const tokenIssuedAt = new Date(tokenResult.issuedAtTime).getTime();
            const now = Date.now();
            const isFreshLogin = (now - tokenIssuedAt) < 30000;

            // Only check session expiration for existing sessions, not fresh logins
            if (!isFreshLogin && userData.sessionStartTime) {
              const state = getSessionState(userData.sessionStartTime);

              if (state === SESSION_STATE.EXPIRED) {
                // Hard expired — force logout
                console.log("Session hard-expired on auth state change, signing out");
                await signOutDueToTimeout();
                if (!isCurrent()) return;
                setCurrentUser(null);
                setIsVerifying(false);
                setLoading(false);
                return;
              }

              // GRACE / GRACE_URGENT — allow in, they'll see the banner
              setSessionState(state);
              setGraceTimeRemaining(getGraceTimeRemaining(userData.sessionStartTime));
              setWarningTimeRemaining(getWarningTimeRemaining(userData.sessionStartTime));
            } else {
              // Fresh sign-ins and legacy profiles without a start time are
              // active. Publish that state before mounting actions that extend
              // the session instead of leaving them blocked until the next poll.
              setSessionState(SESSION_STATE.ACTIVE);
            }

            // Check verification status
            const isGoogleUser = user.providerData.some((provider) => provider.providerId === "google.com");
            const isSignupInProgress = sessionStorage.getItem("signupInProgress") === "true";

            // Google authentication is verified by Firebase Auth. Email/password
            // users may use an explicitly attributed admin override.
            if (!isGoogleUser) {
              // For email/password users, check verification status
              const isManuallyVerified = userData.emailVerified === true
                && !!userData.manuallyVerifiedBy
                && !!userData.manuallyVerifiedAt;

              if (!user.emailVerified && !isManuallyVerified && !isSignupInProgress) {
                console.log("Email not verified by Firebase Auth or an attributed admin override, signing out");
                await auth.signOut();
                if (!isCurrent()) return;
                setCurrentUser(null);
                setIsVerifying(false);
                setLoading(false);
                return;
              }

            }

            // If we get here, user is verified and session is valid
            // Don't set current user during signup to prevent component mounting
            if (!isSignupInProgress) {
              verifiedUid.current = user.uid;
              setAccountRecord({ uid: user.uid, data: userData });
              setCurrentUser(user);
              startSessionMonitoring();
              // Bookkeeping must not hold verified UI behind a pending write.
              void import("./firestoreService")
                .then(({ updateLastLoginTime }) => {
                  if (isCurrent() && verifiedUid.current === user.uid) return updateLastLoginTime(user.uid);
                  return undefined;
                })
                .catch((loginTimeError) => {
                  console.error("Error updating last login time in auth context:", loginTimeError);
                });
            }
            setIsVerifying(false);
          } else {
            await auth.signOut();
            if (!isCurrent()) return;
            setCurrentUser(null);
            setIsVerifying(false);
          }
        } catch (error) {
          if (!isCurrent()) return;
          console.error("Error checking user document:", error);
          // Sign out on any error during user document check
          await auth.signOut();
          if (!isCurrent()) return;
          setCurrentUser(null);
          setIsVerifying(false);
        }
      } else {
        clearPatientDirectory();
        setCurrentUser(null);
        setIsVerifying(false);
        setSessionState(null);
        setGraceTimeRemaining(null);
        stopSessionMonitoring();
      }
      if (isCurrent()) setLoading(false);
    };
    const unsubscribe = auth.onAuthStateChanged(verifySession);
    const finishSignup = () => verifySession(auth.currentUser);
    window.addEventListener('signup-completed', finishSignup);

    // Cleanup on unmount
    return () => {
      disposed = true;
      verificationGeneration.current += 1;
      verifiedUid.current = null;
      unsubscribe();
      window.removeEventListener('signup-completed', finishSignup);
      stopSessionMonitoring();
    };
  }, [startSessionMonitoring, stopSessionMonitoring]);

  const handleSignOut = async () => {
    verificationGeneration.current += 1;
    verifiedUid.current = null;
    setAccountRecord(null);
    setCurrentUser(null);
    stopSessionMonitoring();
    clearPatientDirectory(currentUser?.uid);
    await signOut(auth);
  };

  const value = {
    currentUser,
    accountData: currentUser && accountRecord?.uid === currentUser.uid ? accountRecord.data : null,
    refreshAccount,
    loading,
    isVerifying,
    signOut: handleSignOut,
    sessionState,
    graceTimeRemaining,
    warningTimeRemaining,
    setActiveOperation,
    extendSessionOnAction,
  };

  return <AuthContext.Provider value={value}>{!loading && children}</AuthContext.Provider>;
};
